import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, AlertDescription, AlertTitle } from './alert';

export interface ToastOptions {
  id?: string;
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive';
  durationMs?: number;
}

interface ToastInternal extends Required<Omit<ToastOptions, 'durationMs'>> {
  expiresAt: number;
}

interface ToastContextValue {
  push: (opts: ToastOptions) => string;
  remove: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: any }) {
  const [toasts, setToasts] = useState<ToastInternal[]>([]);

  // GC loop
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setToasts(prev => prev.filter(t => t.expiresAt > now));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const push = useCallback((opts: ToastOptions) => {
    const id = opts.id || `toast_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setToasts(prev => {
      const existing = prev.find(t => t.id === id);
      if (existing) return prev; // dedupe by id
      return [
        {
          id,
          title: opts.title || '',
          description: opts.description || '',
          variant: opts.variant || 'default',
          expiresAt: Date.now() + (opts.durationMs ?? 6000)
        },
        ...prev
      ];
    });
    return id;
  }, []);

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ push, remove }}>
      {children}
      {createPortal(
        <div className="fixed z-[100] bottom-4 right-4 flex flex-col gap-3 w-80 max-w-[92vw] pointer-events-none">
          {toasts.map(t => (
            <Alert
              key={t.id}
              variant={t.variant}
              className="group pointer-events-auto relative overflow-hidden border/60 bg-gradient-to-br from-background via-background to-muted shadow-xl animate-in slide-in-from-bottom-2 fade-in"
            >
              <div className="flex gap-3 pr-8">
                <span
                  className={`mt-1 h-2.5 w-2.5 rounded-full ${
                    t.variant === 'destructive' ? 'bg-red-500' : 'bg-emerald-500'
                  }`}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  {t.title && <AlertTitle className="truncate">{t.title}</AlertTitle>}
                  {t.description && (
                    <AlertDescription className="mt-1 text-sm text-muted-foreground">
                      {t.description}
                    </AlertDescription>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => remove(t.id)}
                aria-label="Dismiss notification"
              >
                x
              </button>
              <span
                className={`pointer-events-none absolute inset-x-0 bottom-0 h-0.5 ${
                  t.variant === 'destructive' ? 'bg-red-500/70' : 'bg-emerald-500/70'
                }`}
              />
            </Alert>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
